'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'

// A dashboard's date range — the core every period-filtered dashboard shares
// (SIBS Custom Mail Alert + Alerts Analysis, Unified Entries, CTT Pilot
// Overview): the TimeRangePicker (moved here from SibsMailView), its date
// helpers, and useDashboardRange, which defaults to the last 30 days and
// remembers each dashboard's own choice in a cookie across reloads.
// BaseDashboard renders the picker itself from its `timeRange` prop;
// dashboards that aren't a BaseDashboard use DashboardRangePicker directly.

// The SIBS pilot's months (April–September 2026): named in periodLabel and
// stepped through by SibsMailView's PilotMonthStepper.
export const PILOT_MONTHS = [
  { label: 'Apr 2026', from: '2026-04-01', to: '2026-04-30' },
  { label: 'May 2026', from: '2026-05-01', to: '2026-05-31' },
  { label: 'Jun 2026', from: '2026-06-01', to: '2026-06-30' },
  { label: 'Jul 2026', from: '2026-07-01', to: '2026-07-31' },
  { label: 'Aug 2026', from: '2026-08-01', to: '2026-08-31' },
  { label: 'Sep 2026', from: '2026-09-01', to: '2026-09-30' },
] as const

const isoDate = (d: Date) => d.toISOString().slice(0, 10)
export const todayIso = () => isoDate(new Date())
export const daysAgoIso = (daysBack: number) => isoDate(new Date(Date.now() - daysBack * 86400000))

// Named labels ("Today", "Last 30 days") only for ranges that end today —
// they used to be picked by day count alone, so a manual 01/09–30/09 read
// "Last 30 days". A full pilot month reads as its name; anything else as dates.
export function periodLabel(from: string, to: string): string {
  const days = Math.round(
    (new Date(to + 'T00:00').getTime() - new Date(from + 'T00:00').getTime()) / 86400000,
  ) + 1
  const map: Record<number, string> = {
    1: 'Today', 7: 'Last 7 days', 14: 'Last 14 days',
    30: 'Last 30 days', 90: 'Last 90 days',
  }
  if (to === todayIso() && map[days]) return map[days]
  const month = PILOT_MONTHS.find((m) => m.from === from && m.to === to)
  if (month) return month.label
  const fromPt = from.split('-').reverse().join('/')
  const toPt = to.split('-').reverse().join('/')
  return from === to ? fromPt : `${fromPt} – ${toPt}`
}

export const CHART_PRESETS = [
  { label: '7D',  days: 7  },
  { label: '14D', days: 14 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
] as const

export function isoToPtDate(iso: string) {
  const [year, month, day] = iso.split('-')
  return year && month && day ? `${day}/${month}/${year}` : iso
}

function ptDateToIso(value: string) {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!match) return null
  const [, dayRaw, monthRaw, yearRaw] = match
  const day = Number(dayRaw)
  const month = Number(monthRaw)
  const year = Number(yearRaw)
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) return null
  return `${yearRaw}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Controlled picker; DashboardRangePicker (below) wraps it with its own open state.
export function TimeRangePicker({
  from,
  to,
  open,
  disabled,
  isDark,
  border,
  muted,
  onOpenChange,
  onApply,
}: {
  from: string
  to: string
  open: boolean
  disabled?: boolean
  isDark: boolean
  border: string
  muted: string
  onOpenChange: (open: boolean) => void
  onApply: (from: string, to: string) => void
}) {
  const [draftFrom, setDraftFrom] = useState(() => isoToPtDate(from))
  const [draftTo, setDraftTo] = useState(() => isoToPtDate(to))

  useEffect(() => {
    if (!open) return
    setDraftFrom(isoToPtDate(from))
    setDraftTo(isoToPtDate(to))
  }, [from, to, open])

  const today = todayIso()
  const chartDays = Math.round((new Date(to + 'T00:00').getTime() - new Date(from + 'T00:00').getTime()) / 86400000) + 1
  const preset = CHART_PRESETS.find((p) => to === today && chartDays === p.days)
  const display = preset ? `Last ${preset.days} days` : periodLabel(from, to)
  const panel = 'border-border bg-card text-muted-foreground'
  const input = `${border} bg-card text-muted-foreground`
  const draftFromIso = ptDateToIso(draftFrom)
  const draftToIso = ptDateToIso(draftTo)
  const canApply = !!draftFromIso && !!draftToIso && draftFromIso <= today && draftToIso <= today

  const apply = (nextFrom = draftFromIso, nextTo = draftToIso) => {
    if (!nextFrom || !nextTo) return
    const safeFrom = nextFrom <= nextTo ? nextFrom : nextTo
    const safeTo = nextFrom <= nextTo ? nextTo : nextFrom
    onApply(safeFrom, safeTo)
    onOpenChange(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onOpenChange(!open)}
        className={`flex h-7 items-center gap-2 border px-2.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-40 ${border} ${muted} hover:border-signal hover:text-signal`}
      >
        <Calendar className="h-3.5 w-3.5" />
        <span className="max-w-44 truncate">{display}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute right-0 top-full z-40 mt-1 grid w-[520px] grid-cols-[1fr_160px] gap-4 rounded border p-4 shadow-notice ${panel}`}>
          <div>
            <div className={`mb-3 font-mono text-[9px] uppercase tracking-widest ${muted}`}>Absolute time range</div>
            <div className="grid grid-cols-[44px_1fr] items-center gap-2">
              <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>From</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="DD/MM/YYYY"
                value={draftFrom}
                onChange={(e) => setDraftFrom(e.target.value)}
                className={`h-8 rounded border px-2 font-mono text-[12px] ${input}`}
              />
              <span className={`font-mono text-[10px] uppercase tracking-widest ${muted}`}>To</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="DD/MM/YYYY"
                value={draftTo}
                onChange={(e) => setDraftTo(e.target.value)}
                className={`h-8 rounded border px-2 font-mono text-[12px] ${input}`}
              />
            </div>
            <button
              type="button"
              onClick={() => apply()}
              disabled={!canApply}
              className="mt-4 h-8 rounded border border-signal px-3 font-mono text-[10px] uppercase tracking-widest text-signal transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
            >
              Apply time range
            </button>
          </div>

          <div className={`border-l pl-4 border-border`}>
            <div className={`mb-2 font-mono text-[9px] uppercase tracking-widest ${muted}`}>Quick ranges</div>
            <div className="flex flex-col">
              {CHART_PRESETS.map(({ label, days }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => apply(daysAgoIso(days - 1), today)}
                  className={`rounded px-2 py-1.5 text-left font-mono text-[11px] transition-colors ${days === preset?.days ? 'bg-signal-surface text-signal' : `${muted} hover:bg-secondary hover:text-signal`}`}
                >
                  Last {days} days
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Persisted range ─────────────────────────────────────────────────────────

export const DEFAULT_RANGE_DAYS = 30

const COOKIE_PREFIX = 'tf_range_'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

// What the cookie stores is the user's intent, not frozen dates:
//   p:<days>        a quick range ("Last 30 days"), still the last N days tomorrow
//   s:<from>        from a fixed day through today, whatever today is
//   a:<from>:<to>   a fixed absolute range
function encodeRange(from: string, to: string): string {
  const today = todayIso()
  if (to === today) {
    const preset = CHART_PRESETS.find((p) => daysAgoIso(p.days - 1) === from)
    return preset ? `p:${preset.days}` : `s:${from}`
  }
  return `a:${from}:${to}`
}

function decodeRange(value: string | null): { from: string; to: string } | null {
  if (!value) return null
  const [kind, a, b] = value.split(':')
  if (kind === 'p' && CHART_PRESETS.some((p) => String(p.days) === a)) {
    return { from: daysAgoIso(Number(a) - 1), to: todayIso() }
  }
  if (kind === 's' && ISO_DAY.test(a ?? '') && a <= todayIso()) return { from: a, to: todayIso() }
  if (kind === 'a' && ISO_DAY.test(a ?? '') && ISO_DAY.test(b ?? '') && a <= b) return { from: a, to: b }
  return null
}

function readCookie(name: string): string | null {
  try {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(name + '='))
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null
  } catch {
    return null
  }
}

function writeCookie(name: string, value: string) {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
  } catch { /* cookies blocked: the range just isn't remembered */ }
}

export type DashboardRange = {
  from: string
  to: string
  /** Applies a range and remembers it for this dashboard. */
  setRange: (from: string, to: string) => void
}

/** A dashboard's from/to (YYYY-MM-DD, inclusive): the user's last choice on
 * this dashboard (cookie `tf_range_<key>`), else the last 30 days. `key` is
 * per dashboard, e.g. 'sibs-mail' or 'unified-ctt'. */
export function useDashboardRange(key: string): DashboardRange {
  const cookieName = COOKIE_PREFIX + key.replace(/[^A-Za-z0-9_-]/g, '_')
  const [range, setRangeState] = useState(() =>
    (typeof document !== 'undefined' && decodeRange(readCookie(cookieName)))
    || { from: daysAgoIso(DEFAULT_RANGE_DAYS - 1), to: todayIso() },
  )
  const setRange = useCallback((from: string, to: string) => {
    setRangeState({ from, to })
    writeCookie(cookieName, encodeRange(from, to))
  }, [cookieName])
  return { from: range.from, to: range.to, setRange }
}

/** TimeRangePicker with its own open state and click-outside close — what
 * BaseDashboard's `timeRange` renders, and what a non-BaseDashboard header
 * (AlertsAnalysisView) drops in directly. */
export function DashboardRangePicker({
  from,
  to,
  onApply,
  disabled,
  isDark,
  border,
  muted,
}: {
  from: string
  to: string
  onApply: (from: string, to: string) => void
  disabled?: boolean
  isDark: boolean
  border: string
  muted: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  return (
    <div ref={ref}>
      <TimeRangePicker
        from={from}
        to={to}
        open={open}
        disabled={disabled}
        isDark={isDark}
        border={border}
        muted={muted}
        onOpenChange={setOpen}
        onApply={onApply}
      />
    </div>
  )
}
